import { ChevronDown, Edit3, Heart, Package, Share2, Trash2, X } from 'lucide-react';
import ProductCard from '@/components/ProductCard';

// Subtle dotted texture overlay, same SVG used by MemoryBox so Wishlist
// cards match the Keepsakes card aesthetic 1:1.
const DOT_PATTERN = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='3' fill='%23ffffff'/%3E%3C/svg%3E")`;

export default function WishlistCard({
  wishlist,
  products,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onShare,
  onRemoveProduct,
  user,
  darkMode
}) {
  const itemCount = products.length;

  if (!isExpanded) {
    // CLOSED STATE, visually mirrors MemoryBox closed state.
    return (
      <div className="animate-in fade-in zoom-in-95 duration-300">
        <div
          data-testid={`wishlist-card-${wishlist.id}`}
          className="relative p-6 rounded-2xl overflow-hidden cursor-pointer transition-all hover:shadow-xl hover:scale-[1.02]"
          onClick={onToggle}
          style={{
            background: `linear-gradient(135deg, ${wishlist.color} 0%, ${wishlist.color}dd 100%)`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minHeight: '160px'
          }}
        >
          {/* Dotted texture overlay */}
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: DOT_PATTERN }} />
          {/* Vertical & horizontal ribbon strips */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-full" style={{ background: 'rgba(255,255,255,0.3)' }} />
          <div className="absolute top-1/2 left-0 w-full h-6 -translate-y-1/2" style={{ background: 'rgba(255,255,255,0.3)' }} />
          {/* Top bow */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
            <div className="w-6 h-4 rounded-full" style={{ background: 'rgba(255,255,255,0.5)' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(255,255,255,0.6)' }} />
            <div className="w-6 h-4 rounded-full" style={{ background: 'rgba(255,255,255,0.5)' }} />
          </div>
          {/* Centerpiece */}
          <div className="relative z-10 text-center mt-6">
            <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
              <Heart className="w-6 h-6" style={{ color: wishlist.color }} />
            </div>
            <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: 'white' }}>{wishlist.name}</h3>
            <p className="text-sm opacity-90" style={{ color: 'white' }}>Wishlist</p>
          </div>
          {/* Gift count badge */}
          {itemCount > 0 && (
            <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.9)', color: wishlist.color }}>
              {itemCount} {itemCount === 1 ? 'gift' : 'gifts'}
            </div>
          )}
          {/* Open chevron */}
          <div className="absolute bottom-3 left-3">
            <ChevronDown className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
          </div>
        </div>
      </div>
    );
  }

  // OPEN STATE, rendered inside the parent's Dialog modal. Transparent
  // wrapper so the header + content paint the rounded corners directly
  // (mirrors the MemoryBox edge-to-edge fix).
  return (
    <div className="animate-in fade-in zoom-in-95 duration-300">
      <div className="rounded-2xl overflow-hidden" style={{ background: 'transparent', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        {/* Header */}
        <div className="p-4 rounded-t-2xl" style={{ background: wishlist.color }}>
          <div className="flex items-center justify-between">
            <div onClick={onToggle} className="flex items-center gap-3 cursor-pointer flex-1">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <Heart className="w-5 h-5" style={{ color: wishlist.color }} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: 'white' }}>{wishlist.name}</h3>
                <p className="text-xs opacity-80" style={{ color: 'white' }}>Wishlist</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid={`wishlist-share-btn-${wishlist.id}`}
                onClick={(e) => { e.stopPropagation(); onShare && onShare(); }}
                className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 relative"
                title={wishlist.is_shared ? 'Sharing on' : 'Share'}
              >
                <Share2 className="w-4 h-4 text-white" />
                {wishlist.is_shared && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
                    style={{ background: '#10B981', border: '2px solid white' }}
                  />
                )}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                title="Edit wishlist"
              >
                <Edit3 className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                title="Delete wishlist"
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                title="Close"
                data-testid="wishlist-modal-close"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Products Container */}
        <div className="p-6 rounded-b-2xl" style={{ background: darkMode?.isDark ? '#2D4354' : '#F9FAFB' }}>
          {products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: darkMode?.textSecondary }} />
              <p className="text-sm" style={{ color: darkMode?.textSecondary }}>No products yet. Start adding items from the Browse page!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5 sm:gap-x-4 sm:gap-y-6">
              {products.map((product) => (
                <div key={product.id} className="group relative">
                  <button
                    onClick={() => onRemoveProduct(product.id)}
                    className="absolute top-2 right-2 z-10 p-2 rounded-full transition-all hover:scale-110 opacity-0 group-hover:opacity-100"
                    style={{
                      background: 'rgba(0, 0, 0, 0.6)',
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    <X className="w-4 h-4" style={{ color: 'white' }} />
                  </button>
                  <ProductCard
                    product={product}
                    user={user}
                    hideHeartButton={true}
                    savedStatus={{ isSaved: true, recipientId: null }}
                    onSaveToggle={() => {}}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
